import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Box, LinearProgress, MenuItem, Select } from '@mui/material'
import { useAppSelector } from '../../../app/hooks'
import { ui } from '../../../utils/uiTokens'
import { CRM_POLL, crmApi } from '../../../features/crm/crmApi'
import { filterField } from '../../../components/filterSelect/FilterSelect'
import { Card, Muted, PageHead, PeriodBar, Pill, PrimaryButton, StudentTabs, TableWrap, contentSx, pageSx, usePeriod } from '../shared/crmUi'
import { fmtDate, hm, iamUserId, money, subjectsLine } from '../shared/format'
import { useCrmDict } from '../shared/dict'
import { StuState, sellerStage, stuState } from '../shared/stages'
import { EmptyRow } from '../head/HeadCrmNewPage'

const RopStudentsPage = () => {
    const { formatMessage, locale } = useIntl()
    const period = usePeriod('month')
    const [sellerId, setSellerId] = useState(0)
    const [tab, setTab] = useState<StuState>('study')
    const token = useAppSelector((st) => st.user.userInfo.token)
    const ropId = iamUserId(token)
    const { data: managers = [] } = crmApi.useGetManagersQuery({ from: period.from, to: period.to })
    const { data = [], isFetching } = crmApi.useListIntakesQuery(
        { from: period.from, to: period.to, sellerId: (sellerId === -1 ? ropId : sellerId) || undefined },
        { pollingInterval: CRM_POLL },
    )
    const all = useMemo(() => [...data].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)), [data])
    const rows = all.filter((r) => stuState(r) === tab)
    const counts = { study: all.filter((r) => stuState(r) === 'study').length, stop: all.filter((r) => stuState(r) === 'stop').length, ret: all.filter((r) => stuState(r) === 'ret').length }
    const active = all.filter((r) => r.status !== 'RETURNED')
    const turnover = active.reduce((s, r) => s + (r.data?.paidSum || 0), 0)
    const nameOf = (id: number) => managers.find((m) => m.userId === id)?.name
    const dict = useCrmDict()
    const navigate = useNavigate()
    const open = (id: number) => navigate(`/rop/students/${id}`)

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead
                    title={formatMessage({ id: 'crmAllStudents' })}
                    sub={formatMessage({ id: 'crmRopStudentsSub' })}
                    action={<PrimaryButton onClick={() => navigate('/rop/register')}>＋ {formatMessage({ id: 'crmRegisterTitle' })}</PrimaryButton>}
                />
                <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <PeriodBar period={period} />
                    <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Box sx={{ fontWeight: 600, fontSize: '13px' }}>{formatMessage({ id: 'crmManager' })}:</Box>
                        <Select size="small" value={sellerId} onChange={(e) => setSellerId(Number(e.target.value))} sx={{ ...filterField, width: 'auto', minWidth: 200 }}>
                            <MenuItem value={0}>{formatMessage({ id: 'crmPreset_all' })}</MenuItem>
                            <MenuItem value={-1}>{formatMessage({ id: 'crmRopSelf' })}</MenuItem>
                            {managers.map((m) => (
                                <MenuItem key={m.userId} value={m.userId}>
                                    {m.name || '-'}
                                    {m.firedAt ? ` (${formatMessage({ id: 'crmFiredLower' })})` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                        <Muted sx={{ fontSize: '13px' }}>
                            {formatMessage({ id: 'crmStudentsSummary' }, { n: active.length, sum: money(turnover), ret: all.length - active.length })}
                        </Muted>
                    </Box>
                </Card>
                {isFetching && <LinearProgress sx={{ marginBottom: 1 }} />}
                <Card>
                    <StudentTabs value={tab} counts={counts} onChange={setTab} />
                    <TableWrap minWidth={1000}>
                        <table>
                            <thead>
                                <tr>
                                    <th>{formatMessage({ id: 'crmColDate' })}</th>
                                    <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                    <th>{formatMessage({ id: 'crmManager' })}</th>
                                    <th>{formatMessage({ id: 'crmColSubjects' })}</th>
                                    <th>{formatMessage({ id: 'crmColPackage' })}</th>
                                    <th>{formatMessage({ id: 'crmPayment' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmColSum' })}</th>
                                    <th>{formatMessage({ id: 'crmColStage' })}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const st = sellerStage(formatMessage, r)
                                    return (
                                        <tr
                                            key={r.id}
                                            className={r.status === 'RETURNED' ? 'ret' : ''}
                                            tabIndex={0}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => open(r.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    open(r.id)
                                                }
                                            }}
                                        >
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {fmtDate(r.createdAt)}
                                                <Muted>{hm(r.createdAt)}</Muted>
                                            </td>
                                            <td>
                                                <b>{r.data?.studentName}</b>
                                            </td>
                                            <td>{r.sellerName || nameOf(r.sellerId) || '-'}</td>
                                            <td>{subjectsLine(r.subjects, locale)}</td>
                                            <td>{dict.label('PACKAGE', r.data?.packageCode)}</td>
                                            <td>
                                                {dict.label('PAY_TYPE', r.data?.payType)}
                                                <Muted>{dict.list('PAY_METHOD', r.data?.payments)}</Muted>
                                            </td>
                                            <td className="num">
                                                {money(r.data?.paidSum)}
                                                {!!r.data?.discountSum && <Muted>{formatMessage({ id: 'crmDiscountShort' }, { sum: money(r.data.discountSum) })}</Muted>}
                                            </td>
                                            <td>
                                                <Pill tone={st.tone} sx={{ whiteSpace: 'normal' }}>{st.text}</Pill>
                                            </td>
                                            <td className="r" style={{ whiteSpace: 'nowrap', color: ui.pink, fontWeight: 600 }}>
                                                {formatMessage({ id: 'crmDetailsArrow' })}
                                            </td>
                                        </tr>
                                    )
                                })}
                                {!rows.length && <EmptyRow cols={9} text={formatMessage({ id: 'crmPeriodEmpty' })} />}
                            </tbody>
                        </table>
                    </TableWrap>
                </Card>
            </Box>
        </Box>
    )
}

export default RopStudentsPage
